import type { LocalizationContext } from '@athena-lib/i18n';
import type { Figure, FigureData } from '$lib/server/figures/types';
import type { StickerShopMeta } from '$lib/types/StickerShopMeta';

export type TrackEvent = {
  stickerShopMeta: StickerShopMeta;
  localization: LocalizationContext;
};

export type TrackFigure = TrackEvent & {
  figure?: Figure;
};

export type TrackFigureData = TrackEvent & {
  figureData?: FigureData;
};

export type TrackAnalytics = {
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
  eventValue?: number;
};

export const enum GtmEventNames {
  Start = 'gtm.js',
  AcceptCookies = 'accept',
  ViewItemList = 'view_item_list',
  SelectItem = 'select_item',
  ViewItem = 'view_item',
  AddToCart = 'add_to_cart',
  BeginCheckout = 'begin_checkout',
  AnalyticsEvent = 'com.stickerapp.events.GoogleAnalyticsEvent',
  QuoteFormSubmit = 'submit_quote',
  QuoteFormNew = 'new_quote',
  QuoteFormSamplePacks = 'sample_pack',
  QuoteFormShow = 'show_quote',
  QuoteFormComplete = 'complete_quote',
  OpenWizard = 'open_wizard',
  SearchResultClicked = 'search_result_clicked',
  SearchResultViewed = 'search_result_viewed',
  SearchBarOpened = 'search_bar_opened',
  CTAPrimary = 'cta_primary',
  CTASecondary = 'cta_secondary',
  CTATertiary = 'cta_tertiary',
  NewChat = 'new_chat',
  ViewChat = 'view_chat',
  ChatThread = 'chat_thread',
  FormInteraction = 'form_interaction',
}

export const enum GtmVariableNames {
  UserId = 'user_id',
}

export const enum GtmEventActions {
  QuoteFormAction = 'multiple_quote_form',
}

export const enum AnalyticsCategories {
  ScrollDepth = 'scroll depth',
}

export type GtmEvent = {
  event: GtmEventNames;
};

export type GtmVariable = {
  [key in GtmVariableNames]: string | number | boolean;
};

export type GtmEventWithProps = GtmEvent & {
  [key: string]: unknown;
};

export type GtmEcommerceEvent = GtmEvent & {
  ecommerce: {
    items: EcommerceItem[];
  };
};

export type EventParams = {
  action: string;
  label?: string;
  category?: string;
  value?: string;
  variant?: string;
  group?: string;
  signed_in?: boolean;
  page_type?: string;
  default_path?: string;
};

export type Tracking = GtmEvent & {
  event_params: EventParams;
};

export type KlaviyoFormEvent = {
  type: 'stepSubmit' | 'embedOpen' | 'close' | 'open' | 'viewedStep';
  formId: string;
  metaData: unknown;
};

export type AnalyticsEvent = GtmEvent & TrackAnalytics;
export type ProductCategory = 'custom' | 'library';

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category: ProductCategory;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_category6: string;
  item_variant: string;
  price: number;
  quantity: number;
  currency: string;
  index: number;
};

export type DataLayer = Array<GtmEvent | GtmVariable | IArguments>;

export type SearchEvent = GtmEvent & {
  event_params: EventParams & {
    query_id?: string;
    query?: string;
    item_url?: string;
    index?: number;
  };
};

export type CTAEvent = GtmEvent & {
  event_params: EventParams & {
    item_url?: string;
    item_text?: string;
  };
};

export type ChatEvent = GtmEvent & {
  event_params: EventParams & {
    chat_id?: string;
    clicked_index?: string;
    query?: string;
    rate_chat?: string;
    iitem_url?: number;
  };
};

export type FormEvent = GtmEvent & {
  event_params: {
    event_action: string;
    event_group: string;
    event_variant: string;
    error_type?: string;
    error_message?: string;
  };
};

export type QuoteFormEvent = GtmEvent & {
  event_params: EventParams & {
    error_type?: string;
    error_message?: string;
    quote_order_id?: string;
  };
};
